import { call, select } from 'redux-saga/effects';
import { format, parseISO } from 'date-fns';

import LINKS from 'constants/links';
import { SERVER_DATE_FORMAT } from 'constants/logic';
import URLS from 'constants/urls';
import { waitForAppInit } from 'features/App/App.saga';
import { REPORT_TYPES } from 'features/Stats/Stats.constants';
import { getDecagonChatPath, makePath } from 'helpers/link-generation';
import { forceRedirectOutsideReact } from 'helpers/page-utils';

import {
  selectRouteParams,
  selectRouteQuery,
  selectActiveVenueId,
  selectActiveMsInventoryVenue,
} from 'selectors/general';

export function* redirectContactUs(client) {
  const path = getDecagonChatPath(makePath.home());
  yield call(client.navigate, path);
}

export function* redirectDynamicPricing(client) {
  const venueId = yield select(selectActiveVenueId);
  yield call(client.navigate, makePath.editVenueDynamicPricing(venueId));
}

export function* redirectEditClasses(client) {
  const params = yield select(selectRouteParams);
  const { venueId, classId } = params;
  yield call(client.navigate, makePath.editClass(venueId, classId));
}

export function* redirectEditVenueDetails(client) {
  const params = yield select(selectRouteParams);
  const { venueId } = params;
  yield call(client.navigate, makePath.editVenue(venueId));
}

export function* redirectEditVenuePhotos(client) {
  const params = yield select(selectRouteParams);
  const { venueId } = params;
  yield call(client.navigate, makePath.editVenuePhotos(venueId));
}

export function* redirectHome(client) {
  yield call(client.navigate, makePath.home());
}

export function* redirectPike13(client) {
  const params = yield select(selectRouteParams);
  const { venueId } = params;
  yield call(client.navigate, makePath.pike13(venueId));
}

export function* redirectPrintRoster(client) {
  const params = yield select(selectRouteParams);
  const { venueId, scheduleId } = params;

  let newPath;
  if (venueId && scheduleId) {
    newPath = URLS.schedule.print(venueId, scheduleId, format(new Date(), SERVER_DATE_FORMAT));
  } else {
    newPath = makePath.home();
  }

  yield call(client.navigate, newPath);
}

export function* redirectRatings(client) {
  const params = yield select(selectRouteParams);
  const { c: classId, i: instructorId, v: initialVisits } = yield select(selectRouteQuery);
  let { venueId } = params;
  if (!venueId) {
    venueId = yield select(selectActiveVenueId);
  }
  yield call(client.navigate, makePath.ratings({ ...params, venueId, classId, instructorId, initialVisits }));
}

export function* redirectRatingsReviews(client) {
  const params = yield select(selectRouteParams);
  let { venueId } = params;
  if (!venueId) {
    venueId = yield select(selectActiveVenueId);
  }
  yield call(client.navigate, makePath.ratings({ ...params, venueId, tab: 'reviews' }));
}

export function* redirectReports(client) {
  const { reportType, scheduleId } = yield select(selectRouteParams);
  let newPath;
  if (reportType === REPORT_TYPES.CLASSES && scheduleId) {
    newPath = makePath.reports({ reportType, id: scheduleId });
  } else if (reportType === REPORT_TYPES.CLASSES) {
    newPath = makePath.reports({ reportType });
  } else if (reportType === REPORT_TYPES.RESERVATIONS) {
    newPath = makePath.reports({ reportType: REPORT_TYPES.BOOKINGS });
  } else if (reportType === REPORT_TYPES.SUMMARY) {
    newPath = makePath.reports();
  } else {
    newPath = makePath.reports();
  }
  yield call(client.navigate, newPath);
}

export function* redirectStats(client) {
  yield call(waitForAppInit);
  const venueId = yield select(selectActiveVenueId);
  yield call(client.navigate, makePath.stats('your-insights', { venueId }), { replace: true });
}

export function* redirectYieldManagement(client) {
  const params = yield select(selectRouteParams);
  yield call(
    client.navigate,
    makePath.classes({ ...params, startDate: params.startDate ? parseISO(params.startDate) : new Date() })
  );
}

export function* redirectAccountSettings(client) {
  yield call(client.navigate, makePath.accountSettings());
}

export function* redirectPathToPartnership() {
  yield call(forceRedirectOutsideReact, LINKS.signUp.href);
}

export function* redirectWithVenueId(makePathFactory, client) {
  const venueId = yield select(selectActiveVenueId);
  const { hash } = client.location;
  const [pathname, search] = makePathFactory(venueId).split('?');
  yield call(client.navigate, { pathname, search: search ? `?${search}` : '', hash });
}

export function* redirectWithMSIVenueId(makePathFactory, client) {
  const venue = yield select(selectActiveMsInventoryVenue);
  const { hash } = client.location;
  const [pathname, search] = makePathFactory(venue.venueId).split('?');
  yield call(client.navigate, { pathname, search: search ? `?${search}` : '', hash });
}

export function* redirect(to, client) {
  yield call(client.navigate, to);
}
